import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { AiAssistantService } from '../services/ai-assistant.service'
// Deep-import light utils — NEVER '../utils' (its barrel re-exports markdown.ts,
// whose top-level hljs.registerLanguage side effects drag marked/DOMPurify/
// highlight.js into any chunk that eagerly reaches this store — the FAB does).
import { createTypewriter } from '../utils/typewriter'
import { genMsgId, genThreadId } from '../utils/ids'
import type {
  ChatMessage,
  Conversation,
  ReplaceDialogState,
  SseCallbacks,
  StreamHandle,
  VoteValue,
} from '../types/ai-assistant.types'

const PLACEHOLDER_ID = 'placeholder'

function blankAssistant(): ChatMessage {
  return {
    id: genMsgId(),
    role: 'assistant',
    content: '',
    thinking: '',
    toolUses: [],
    approval: null,
    approvalDecided: false,
    timestamp: new Date(),
    vote: null,
  }
}

/**
 * DEV-7125 — shared AI chat state (lifted from `useAiChat`, which is now a thin
 * wrapper over this store so the /ai-assistant page and the global drawer stay
 * in sync). Streams deliberately survive route changes and drawer close (no
 * unmount abort); state lives for the SPA session and is cleared only via
 * `$reset()` — in-memory state otherwise dies with the logout hard redirect.
 */
export const useAiChatStore = defineStore('aiChat', () => {
  const service = new AiAssistantService()

  const conversations = ref<Conversation[]>([])
  const activeConversationId = ref('')
  const isStreaming = ref(false)
  /** True from send() entry until the stream starts (or send fails). */
  const isSending = ref(false)
  /** Id of the conversation an active stream writes into (may differ from active). */
  const streamingConversationId = ref<string | null>(null)
  const showTypingIndicator = ref(false)
  /** SessionId whose history fetch is in flight (empty-history loads only). */
  const loadingHistoryId = ref<string | null>(null)
  /** True only after a listSessions() round-trip succeeded. */
  const sessionsLoaded = ref(false)
  const replaceDialog = ref<ReplaceDialogState>({ open: false, files: [] })

  let streamHandle: StreamHandle | null = null
  let typewriter: ReturnType<typeof createTypewriter> | null = null
  let replaceResolver: ((value: boolean) => void) | null = null
  let sessionsPromise: Promise<void> | null = null
  /** Session ids with an in-flight PATCH (rename/pin), skipped by the merge. */
  const pendingUpdates = new Set<string>()

  const isBusy = computed(() => isStreaming.value || isSending.value)
  const isLoadingHistory = computed(() => loadingHistoryId.value === activeConversationId.value)

  const activeConversation = computed(() =>
    conversations.value.find((c) => c.id === activeConversationId.value),
  )
  const messages = computed(() => activeConversation.value?.messages ?? [])
  const conversationTitle = computed(() => activeConversation.value?.title ?? 'New Chat')
  const sortedConversations = computed(() =>
    [...conversations.value].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()),
  )

  function deriveTitle(text: string): string {
    return text.length > 50 ? text.substring(0, 50) + '...' : text
  }

  // --- Session list / history ---

  /**
   * Merge-safe, retryable session loading.
   * - Merge, don't replace: existing Conversation objects are kept (their loaded
   *   messages and any in-flight stream reference stay alive); titles/pinned/
   *   timestamps update in place; new sessions are added; server-deleted sessions
   *   are removed unless placeholder / active / streaming target.
   * - Precedence: preferred → current-if-valid → most recent → newChat.
   */
  async function loadSessions(preferredSessionId?: string): Promise<void> {
    try {
      const sessions = await service.listSessions()
      const byId = new Map(conversations.value.map((c) => [c.id, c]))
      const seen = new Set<string>()
      for (const s of sessions) {
        seen.add(s.sessionId)
        const existing = byId.get(s.sessionId)
        if (existing) {
          if (!pendingUpdates.has(s.sessionId)) {
            // Don't revert an optimistic rename/pin racing its own PATCH.
            existing.title = s.title
            existing.pinned = !!s.pinned
          }
          existing.timestamp = new Date(s.lastMessageDate)
        } else {
          conversations.value.push({
            id: s.sessionId,
            threadId: s.sessionId,
            title: s.title,
            preview: '',
            timestamp: new Date(s.lastMessageDate),
            pinned: !!s.pinned,
            messages: [],
          })
        }
      }
      // Remove server-deleted conversations — but never the placeholder, the active
      // conversation, or the streaming target (stream callbacks hold direct refs).
      conversations.value = conversations.value.filter(
        (c) =>
          seen.has(c.id) ||
          c.id === PLACEHOLDER_ID ||
          c.id === activeConversationId.value ||
          c.id === streamingConversationId.value,
      )
      sessionsLoaded.value = true
      // Precedence: preferred → current-if-valid → most recent → newChat.
      // Auto-select deliberately uses sortedConversations[0] (most-recent).
      if (preferredSessionId && restoreSelection(preferredSessionId)) return
      if (conversations.value.some((c) => c.id === activeConversationId.value)) return
      if (sortedConversations.value.length) selectConversation(sortedConversations.value[0].id)
      else newChat()
    } catch {
      sessionsPromise = null // transient failure must be retryable on next open
      if (conversations.value.length === 0) newChat()
    }
  }

  function ensureSessionsLoaded(preferredSessionId?: string): Promise<void> {
    // `preferred` only influences the INITIAL load; applyScreenMapping (useAiPanel)
    // is the sole selection owner afterwards — no double select/fetch on reopen.
    if (!sessionsPromise) sessionsPromise = loadSessions(preferredSessionId)
    return sessionsPromise
  }

  /** Validated select: false when the id is unknown OR selection is blocked (busy). */
  function restoreSelection(sessionId: string): boolean {
    if (sessionId === activeConversationId.value) {
      // Already active: refetch only when an earlier (silently-failed) history
      // fetch left a threadId'd conversation empty — gives it a retry path.
      // Skip while a fetch for it is already in flight (first-open double-select).
      const conv = activeConversation.value
      if (conv?.threadId && conv.messages.length === 0 && loadingHistoryId.value !== conv.id) {
        void fetchMessages(conv)
      }
      return true
    }
    if (isBusy.value) return false
    if (!conversations.value.some((c) => c.id === sessionId)) return false
    selectConversation(sessionId)
    return true
  }

  async function fetchMessages(conv: Conversation): Promise<void> {
    if (!conv.threadId) return
    const lenAtStart = conv.messages.length
    if (lenAtStart === 0) loadingHistoryId.value = conv.id
    try {
      const data = await service.getSessionMessages(conv.threadId)
      // Discard if messages changed underneath (optimistic bubble/stream) or the
      // conversation is the streaming/sending target — never detach them:
      if (conv.messages.length !== lenAtStart) return
      if (conv.id === streamingConversationId.value || isSending.value) return
      conv.messages = (data.messages ?? []).map((m) => ({
        id: genMsgId(),
        role: m.role,
        content: m.content || '',
        thinking: '',
        toolUses: [],
        approval: null,
        approvalDecided: false,
        timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
        vote: null,
        files: m.files?.length ? m.files.map((name) => ({ name })) : undefined,
      }))
    } catch {
      /* non-fatal — leave the conversation's messages as-is */
    } finally {
      if (loadingHistoryId.value === conv.id) loadingHistoryId.value = null
    }
  }

  function selectConversation(convId: string): void {
    if (isBusy.value) return
    activeConversationId.value = convId
    const conv = conversations.value.find((c) => c.id === convId)
    if (conv && conv.threadId) fetchMessages(conv)
  }

  function newChat(): void {
    if (isBusy.value) return
    const existing = conversations.value.find((c) => c.id === PLACEHOLDER_ID)
    if (existing) {
      existing.messages = []
      activeConversationId.value = PLACEHOLDER_ID
      return
    }
    conversations.value.unshift({
      id: PLACEHOLDER_ID,
      threadId: null,
      title: 'New Chat',
      preview: '',
      timestamp: new Date(),
      pinned: false,
      messages: [],
    })
    activeConversationId.value = PLACEHOLDER_ID
  }

  function deleteConversation(convId: string): void {
    // Deleting the active conversation mid-send would let the createSession
    // continuation rebind a spliced-out object and stream invisibly while
    // holding isBusy — refuse during the send window.
    if (isSending.value && convId === activeConversationId.value) return
    // Deleting the streaming conversation (active or not) detaches the typewriter
    // target — stop the stream first.
    if (convId === streamingConversationId.value) stopGeneration()
    const idx = conversations.value.findIndex((c) => c.id === convId)
    if (idx === -1) return
    const conv = conversations.value[idx]
    conversations.value.splice(idx, 1)
    if (conv.threadId) service.deleteSession(conv.threadId).catch(() => {})
    if (activeConversationId.value === convId) {
      activeConversationId.value = conversations.value.length ? conversations.value[0].id : ''
      if (!activeConversationId.value) newChat()
      else {
        const next = activeConversation.value
        if (next?.threadId) fetchMessages(next)
      }
    }
  }

  function renameConversation(payload: { id: string; title: string }): void {
    const conv = conversations.value.find((c) => c.id === payload.id)
    if (!conv) return
    conv.title = payload.title
    if (conv.threadId) {
      pendingUpdates.add(conv.id)
      service
        .updateSession(conv.threadId, { title: payload.title })
        .catch(() => {})
        .finally(() => pendingUpdates.delete(conv.id))
    }
  }

  function togglePin(convId: string): void {
    const conv = conversations.value.find((c) => c.id === convId)
    if (!conv) return
    conv.pinned = !conv.pinned
    if (conv.threadId) {
      pendingUpdates.add(conv.id)
      service
        .updateSession(conv.threadId, { pinned: conv.pinned })
        .catch(() => {})
        .finally(() => pendingUpdates.delete(conv.id))
    }
  }

  // --- Streaming ---

  function markToolsCompleted(msg: ChatMessage): void {
    if (!msg.toolUses.length) return
    msg.toolUses = msg.toolUses.map((t) => ({ ...t, completed: true }))
  }

  function streamCallbacks(msg: ChatMessage): SseCallbacks {
    return {
      onToken: (token) => {
        showTypingIndicator.value = false
        typewriter?.push(token)
      },
      onToolUse: (info) => {
        showTypingIndicator.value = false
        msg.toolUses = [
          ...msg.toolUses,
          { name: info.name, args: info.args, icon: 'fa-solid fa-wrench', completed: false },
        ]
      },
      onApprovalRequired: (data) => {
        showTypingIndicator.value = false
        typewriter?.flush()
        msg.approval = data
        msg.approvalDecided = false
        isStreaming.value = false
        streamingConversationId.value = null
        streamHandle = null
        typewriter = null
      },
      onDone: () => {
        showTypingIndicator.value = false
        streamHandle = null
        markToolsCompleted(msg)
        typewriter?.finish(() => {
          isStreaming.value = false
          streamingConversationId.value = null
          typewriter = null
        })
      },
      onError: (errMsg) => {
        showTypingIndicator.value = false
        typewriter?.stop()
        msg.content = errMsg
        isStreaming.value = false
        streamingConversationId.value = null
        streamHandle = null
        typewriter = null
      },
    }
  }

  /** Push an assistant bubble into `conv` and stream `streamFn`'s response into it. */
  function streamInto(conv: Conversation, streamFn: (msg: ChatMessage) => StreamHandle): void {
    conv.messages.push(blankAssistant())
    const msg = conv.messages[conv.messages.length - 1]
    isStreaming.value = true
    isSending.value = false // the stream has started — send window over
    streamingConversationId.value = conv.id
    showTypingIndicator.value = true
    typewriter = createTypewriter((chunk) => {
      msg.content += chunk
    })
    streamHandle = streamFn(msg)
  }

  function sendToAgent(conv: Conversation, promptText: string, fileNames?: string[]): void {
    streamInto(conv, (msg) =>
      service.invokeAgent({
        prompt: promptText,
        threadId: conv.threadId as string,
        fileNames,
        ...streamCallbacks(msg),
      }),
    )
  }

  // --- File upload ---

  function confirmReplace(files: string[]): Promise<boolean> {
    return new Promise((resolve) => {
      replaceResolver = resolve
      replaceDialog.value = { open: true, files }
    })
  }

  function onReplaceConfirm(decision: boolean): void {
    const resolver = replaceResolver
    replaceResolver = null
    replaceDialog.value = { open: false, files: [] }
    resolver?.(decision)
  }

  /**
   * Upload the attached files, prompting to replace any duplicates. Returns the
   * accepted file names to thread into the prompt. Encryption is server-side, so
   * the client just sends the raw files.
   */
  async function ensureFilesUploaded(sessionId: string, files: File[]): Promise<string[]> {
    if (!files.length) return []

    const resp = await service.uploadFiles(sessionId, files, false)
    const list = resp.files ?? []
    const ok = list.filter((f) => f.status === 'ok').map((f) => f.name)
    const dups = list.filter((f) => f.status === 'duplicate').map((f) => f.name)
    if (!dups.length) return ok

    const replace = await confirmReplace(dups)
    if (!replace) return ok

    const dupFiles = files.filter((f) => dups.includes(f.name))
    const forceResp = await service.uploadFiles(sessionId, dupFiles, true)
    const moreOk = (forceResp.files ?? []).filter((f) => f.status === 'ok').map((f) => f.name)
    return ok.concat(moreOk)
  }

  /** Returns the sessionId the message was streamed into ('' on failure/rollback). */
  async function send(text: string, files: File[] = []): Promise<string> {
    if (isBusy.value) return ''
    const trimmed = text.trim()
    if (!trimmed && !files.length) return ''

    if (!activeConversation.value) newChat()
    const conv = activeConversation.value
    if (!conv) return ''

    isSending.value = true
    try {
      if (!conv.threadId) {
        const created = await service.createSession(
          deriveTitle(trimmed) || 'New Chat',
          genThreadId(),
        )
        conv.id = created.sessionId
        conv.threadId = created.sessionId
        conv.title = created.title
        conv.timestamp = new Date(created.lastMessageDate)
        // Rebind the selection ONLY if the placeholder is still what's active —
        // a concurrent restore must not be yanked back.
        if (activeConversationId.value === PLACEHOLDER_ID) {
          activeConversationId.value = created.sessionId
        }
      }

      if (conv.title === 'New Chat' && deriveTitle(trimmed)) {
        conv.title = deriveTitle(trimmed)
        if (conv.threadId)
          service.updateSession(conv.threadId, { title: conv.title }).catch(() => {})
      }

      conv.preview = trimmed.length > 60 ? trimmed.substring(0, 60) + '...' : trimmed
      conv.timestamp = new Date()
      conv.messages.push({
        id: genMsgId(),
        role: 'user',
        content: trimmed,
        thinking: '',
        toolUses: [],
        approval: null,
        approvalDecided: false,
        timestamp: new Date(),
        vote: null,
        files: files.length ? files.map((f) => ({ name: f.name })) : undefined,
      })
      const userMsg = conv.messages[conv.messages.length - 1]

      const acceptedNames = await ensureFilesUploaded(conv.threadId as string, files)
      // $reset()-mid-send guard: if the store was reset (or the conv otherwise
      // detached) while awaiting, do NOT stream into a detached object.
      if (!conversations.value.includes(conv)) {
        isSending.value = false
        return ''
      }
      // Bedrock rejects empty user content — roll back the optimistic bubble.
      if (!trimmed && acceptedNames.length === 0) {
        const idx = conv.messages.indexOf(userMsg)
        if (idx >= 0) conv.messages.splice(idx, 1)
        isSending.value = false
        return ''
      }
      sendToAgent(conv, trimmed, acceptedNames.length ? acceptedNames : undefined)
      return conv.id
    } catch {
      /* createSession/upload failed — leave the optimistic bubble; no stream started */
      isSending.value = false
      return ''
    }
  }

  function handleApproval(messageId: string, decision: 'approved' | 'rejected'): void {
    if (isBusy.value) return
    const conv = activeConversation.value
    if (!conv) return
    const target = conv.messages.find((m) => m.id === messageId)
    if (target) target.approvalDecided = true

    streamInto(conv, (msg) =>
      service.sendApproval({
        decision,
        threadId: conv.threadId as string,
        ...streamCallbacks(msg),
      }),
    )
  }

  function stopGeneration(): void {
    typewriter?.flush()
    typewriter = null
    streamHandle?.abort()
    streamHandle = null
    isStreaming.value = false
    isSending.value = false
    streamingConversationId.value = null
    showTypingIndicator.value = false
  }

  function regenerate(): void {
    if (isBusy.value) return
    const conv = activeConversation.value
    if (!conv || conv.messages.length < 2) return
    const last = conv.messages[conv.messages.length - 1]
    if (last.role === 'assistant') conv.messages.pop()

    let lastUser: ChatMessage | null = null
    for (let i = conv.messages.length - 1; i >= 0; i--) {
      if (conv.messages[i].role === 'user') {
        lastUser = conv.messages[i]
        break
      }
    }
    if (lastUser) sendToAgent(conv, lastUser.content)
  }

  function vote(messageId: string, value: VoteValue): void {
    const target = activeConversation.value?.messages.find((m) => m.id === messageId)
    if (target) target.vote = value
  }

  function $reset(): void {
    stopGeneration()
    if (replaceResolver) onReplaceConfirm(false) // never leave the upload promise hanging
    conversations.value = []
    activeConversationId.value = ''
    isSending.value = false
    streamingConversationId.value = null
    loadingHistoryId.value = null
    sessionsLoaded.value = false
    sessionsPromise = null
    pendingUpdates.clear()
    replaceDialog.value = { open: false, files: [] }
  }

  return {
    // state + computeds
    conversations,
    sortedConversations,
    activeConversation,
    activeConversationId,
    messages,
    conversationTitle,
    isStreaming,
    isSending,
    isBusy,
    streamingConversationId,
    showTypingIndicator,
    loadingHistoryId,
    isLoadingHistory,
    sessionsLoaded,
    replaceDialog,
    // session list / history
    loadSessions,
    ensureSessionsLoaded,
    restoreSelection,
    selectConversation,
    newChat,
    deleteConversation,
    renameConversation,
    togglePin,
    // chat
    send,
    handleApproval,
    stopGeneration,
    regenerate,
    vote,
    onReplaceConfirm,
    $reset,
  }
})
